import { Signal, useSignal } from "@preact/signals";
import { useCallback, useEffect, useMemo, useRef } from "preact/hooks";
import { formatPrice } from "$store/sdk/format.ts";
import { address, setAddress } from "$store/sdk/shipping.ts";
import { useCart } from "apps/vtex/hooks/useCart.ts";
import Button from "$store/components/ui/Button.tsx";
import type { SimulationOrderForm, Sla } from "apps/vtex/utils/types.ts";
import { invoke } from "site/runtime.ts";
import { fixMaxLengthWhenNumberInput } from "$store/sdk/format.ts";

const formatShippingEstimate = (estimate: string) => {
  const [, time, type] = estimate.split(/(\d+)/);

  if (type === "bd") {
    return Number(time) > 1 ? `${time} dias úteis` : `${time} dia útil`;
  }

  if (type === "d") {
    return Number(time) > 1 ? `${time} dias` : `${time} dia`;
  }

  if (type === "h") {
    return Number(time) > 1 ? `${time} horas` : `${time} hora`;
  }
};

function ShippingContent({
  simulation,
}: {
  simulation: Signal<SimulationOrderForm | null>;
}) {
  const { cart } = useCart();

  const methods = simulation.value?.logisticsInfo
    ?.reduce((initial, { slas }) => [...initial, ...slas], [] as Sla[])
    .reduce((slas, sla) => {
      const index = slas.findIndex((item) => item.id === sla.id);

      if (index !== -1) {
        slas[index].price += sla.price;
        return slas;
      }

      return [...slas, sla];
    }, [] as Sla[])
    .flat() ?? [];

  const locale = cart.value?.clientPreferencesData.locale || "pt-BR";
  const currencyCode = cart.value?.storePreferencesData.currencyCode || "BRL";

  if (simulation.value == null) return null;

  if (
    !methods.length &&
    simulation.value.messages.find(
      (message) => message.code === "cannotBeDelivered",
    )
  ) {
    return (
      <div class="py-4 my-4 border-y border-tw-neutral-100">
        <span class="text-tw-neutral-500 text-sm">
          Entrega indisponível para o CEP informado!
        </span>
      </div>
    );
  }

  if (methods.length === 0) {
    return (
      <div class="py-4 my-4 border-y border-tw-neutral-100">
        <span class="text-tw-neutral-500 text-sm lg:max-2xl:text-xs">
          CEP inválido!
        </span>
      </div>
    );
  }

  return (
    <ul class="py-4 my-4 border-y border-tw-neutral-100 grid gap-2 lg:max-2xl:py-2 lg:max-2xl:my-2 lg:max-2xl:gap-1">
      {methods.slice(0, 4).map((method) => (
        <li class="text-tw-neutral-500 text-sm flex justify-between items-center lg:max-2xl:text-xs">
          <span class="font-bold basis-36">
            {method.pickupStoreInfo.isPickupStore
              ? "Retirada"
              : `Em até ${formatShippingEstimate(method.shippingEstimate)}`}
          </span>

          <span class="text-center">
            {method.pickupStoreInfo.isPickupStore
              ? "Retirada na loja"
              : method.name}
          </span>

          <span class="font-bold text-right basis-20">
            {method.price === 0
              ? "Grátis"
              : formatPrice(method.price / 100, currencyCode, locale)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ShippingSimulation() {
  const inputRef = useRef<HTMLInputElement>(null);
  const loading = useSignal(false);
  const simulateResult = useSignal<SimulationOrderForm | null>(null);
  const { simulate, cart } = useCart();

  const items = useMemo(
    () =>
      cart.value!.items.map((item) => {
        return {
          id: Number(item.id),
          quantity: item.quantity,
          seller: item.seller,
        };
      }),
    [cart.value?.items],
  );

  const handleSimulation = useCallback(async () => {
    if (
      inputRef.current?.value.length !== 8 &&
      address.value.postalCode.length !== 8
    ) {
      return;
    }

    const postalCode = inputRef.current?.value ?? address.value.postalCode;

    try {
      loading.value = true;

      await invoke["site"].actions.updateShippingData({
        country: cart.value?.storePreferencesData.countryCode || "BRA",
        postalCode,
      });

      simulateResult.value = await simulate({
        items: items,
        postalCode,
        country: cart.value?.storePreferencesData.countryCode || "BRA",
      });
    } finally {
      if (
        address.value.postalCode !==
          (inputRef.current?.value ?? address.value.postalCode)
      ) {
        await setAddress(postalCode);
      }

      loading.value = false;
    }
  }, [items]);

  // Recalculates the shipping value
  // every time the array of items changes
  useEffect(() => {
    if (!loading.value && inputRef.current?.value) {
      handleSimulation();
    }
  }, [items]);

  // If a postal code is defined in the
  // cookies, use it for the simulation
  useEffect(() => {
    if (!inputRef.current) return;

    if (address.value.postalCode) {
      inputRef.current.value = address.value.postalCode;
      handleSimulation();
    }
  }, []);

  return (
    <div class="mt-4 lg:max-2xl:mt-2">
      <div class="flex justify-between items-center gap-6">
        <span class="text-tw-neutral-600 text-sm font-bold whitespace-nowrap lg:max-2xl:text-xs">
          Calcule o frete:
        </span>

        {!simulateResult.value?.logisticsInfo?.length
          ? (
            <form
              class="h-12 border border-tw-base-100 rounded-full bg-white flex justify-between items-center"
              onSubmit={(e) => {
                e.preventDefault();
                handleSimulation();
              }}
            >
              <input
                class="w-full h-full px-4 rounded-full outline-none text-sm placeholder:text-tw-neutral-300"
                ref={inputRef}
                type="text"
                placeholder="Digite seu CEP"
                maxLength={8}
                onInput={fixMaxLengthWhenNumberInput}
              />

              <Button
                class="w-24 h-full rounded-full bg-tw-primary-100 text-sm text-tw-base-100 font-bold shrink-0"
                type="submit"
              >
                Calcular
              </Button>
            </form>
          )
          : (
            <div class="text-sm flex items-center gap-2 lg:max-2xl:text-xs">
              <span class="text-tw-neutral-600">
                {simulateResult.value.postalCode}
              </span>

              <button onClick={() => (simulateResult.value = null)}>X</button>
            </div>
          )}
      </div>

      <ShippingContent simulation={simulateResult} />
    </div>
  );
}

export default ShippingSimulation;
